import { useMemo } from 'react'

import { Button, Heading, theme } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Box } from '@mantine/core'
import { useIntl } from 'react-intl'
import sanitizeHtml from 'sanitize-html'

import { getHelloSitesUrl } from 'api/utils/paths'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath } from 'interfaces/constants'
import { WeekOfPregnancy } from 'interfaces/types'

import { StyledWeekDetailArea } from './index.styled'

type WeekDetailProps = {
  currentWeekDetail: WeekOfPregnancy
  disabledRecommendation?: boolean
}
export const WeekDetail = ({
  currentWeekDetail,
  disabledRecommendation,
}: WeekDetailProps) => {
  const { locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()

  const values = useMemo(
    () =>
      [
        {
          label: f({
            id: 'weekOfPregnancy.size.height',
            defaultMessage: 'Height:',
          }),
          value: currentWeekDetail.height,
          unit: 'cm',
        },
        {
          label: f({
            id: 'weekOfPregnancy.size.weight',
            defaultMessage: 'Weight:',
          }),
          value: currentWeekDetail.weight,
          unit: 'kg',
        },
      ].filter((i) => i.value && i.value !== '0'),
    [currentWeekDetail],
  )

  return (
    <StyledWeekDetailArea>
      <div className="infant-image">
        <img
          loading="lazy"
          src={currentWeekDetail.icon || `${assetImagePath}/infant.png`}
          alt="Infant"
        />
      </div>
      {currentWeekDetail.description && currentWeekDetail.name && (
        <div className="detail">
          <Heading tag="h4" color={theme.colors.gray800}>
            {currentWeekDetail.name}
          </Heading>
          <Text as="span" size="p3" color={theme.colors.gray700}>
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(currentWeekDetail.description, {
                  allowedTags: false,
                  allowedAttributes: false,
                  allowVulnerableTags: true,
                }),
              }}
            ></div>
            <Text as="span" size="p3">
              {values.map((i, idx) => (
                <Box
                  component="span"
                  key={String(idx)}
                  sx={(theme) => ({
                    [theme.fn.smallerThan('sm')]: {
                      display: 'flex',
                      flexDirection: 'column',
                      '.divider': {
                        display: 'none',
                      },
                    },
                  })}
                >
                  <span>
                    {i.label}{' '}
                    <Text size="s2" weight="semiBold" as="span">
                      {i.value} {i.unit}
                    </Text>
                  </span>
                  {idx < values.length - 1 && (
                    <span className="divider">{' - '}</span>
                  )}
                </Box>
              ))}
            </Text>
          </Text>

          {Boolean(currentWeekDetail.permalink) && !disabledRecommendation && (
            <Button
              size="lg"
              color="transparent"
              className="non-pointer-event-child"
              dataEventCategory="Pregnancy Progression"
              dataEventAction="Recommendation Click"
              dataEventLabel={
                `${getHelloSitesUrl(locale)}${currentWeekDetail.permalink}` ||
                '#'
              }
              onClick={() => {
                if (currentWeekDetail.permalink) {
                  window.location.href = currentWeekDetail.permalink
                }
              }}
            >
              <Text size="s3" weight="semiBold" color={theme.colors.pink800}>
                {f({
                  id: 'weekOfPregnancy.recommendations',
                  defaultMessage: 'Recommendation',
                })}
              </Text>
            </Button>
          )}
        </div>
      )}
    </StyledWeekDetailArea>
  )
}
